import { DTOModelService } from "@cervval.js/common-dto/src/dtoModel.service";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { TypeInfos } from "@cervval.js/ts-reflect";
import { FieldMarshaller, pushTableDefinitions, SqlColumn, SqlDbDefinition, SqlDbDefinitionMap, SqlForeignKey, SqlTableDefinition } from "./sql.common";

//#region Build table definition

/** 
 * Initialize the helper from the DTOModelService. 
 * If not passed in argument, it will be get from the injector.
 */
export function buildSQLDBDefinitionFromDTOModel<ET, FT>(marshallers: Map<keyof FT, string | FieldMarshaller>, dtoModel: DTOModelService<ET, FT>): SqlDbDefinition<keyof ET, keyof FT> {
    type TableName = keyof ET;
    type FieldTypeName = keyof FT;

    const dbDefinition: SqlDbDefinition<TableName, FieldTypeName> = {
        marshallers,
        tables: []
    };

    const tables: Map<TableName, {
        getTableDefinition: () => SqlTableDefinition<TableName, FieldTypeName>,
        typeInfos: TypeInfos
    }> = new Map();

    for (const tablename of dtoModel.tableNames) {
        tables.set(tablename, {
            getTableDefinition: () => buildTableDefinition<ET, FT>(tablename, dtoModel),
            typeInfos: dtoModel.getTableTypeInfos(tablename, { withExtras: false }) // We only want properties from the DTO, others are not stored in DB
        });
    }

    pushTableDefinitions(dbDefinition.tables, tables, { checkTableDefinitions: true });

    return dbDefinition;
}

/** 
 * Generate columns from given type. 
 * 
 * @throws An error if table name is unknown
 * @throws A message if we cannot retrieve the type of a property
 */
export function buildTableDefinition<ET, FT>(tableName: keyof ET, dtoModel: DTOModelService<ET, FT>): SqlTableDefinition<keyof ET, keyof FT> {
    type TableName = keyof ET;
    type FieldTypeName = keyof FT;

    const definitions: SqlTableDefinition<TableName, FieldTypeName> = {
        name: tableName,
        columns: [],
        associations: {}
    };
    if (!dtoModel.hasTable(tableName as string)) {
        // Here it is important to get the stack trace to understand where the call came from.
        throw new Error(`Invalid table name: ${tableName as string}`);
    }

    for (const propertyNameStr of dtoModel.getTableFields(tableName, { withExtras: false })) { // Here we only want the original properties from the DTO
        const propertyName = propertyNameStr as keyof ET[TableName];

        const fieldAssociation = dtoModel.getTableFieldAssociation(tableName, propertyName);

        if (fieldAssociation && definitions.associations) {
            const ownerIdField = dtoModel.getTableIdField(fieldAssociation.ownerColumn.foreignTable);

            const associateTable = getProp<keyof ET | undefined>(fieldAssociation.associateColumn, "foreignTable");
            let associateFieldType = getProp<string | undefined>(fieldAssociation.associateColumn, "fieldType");
            const associateIdField = associateTable ? dtoModel.getTableIdField(associateTable) : undefined;
            const associateColumnName = fieldAssociation.associateColumn.name;

            if (associateIdField && associateTable) {
                associateFieldType = String(dtoModel.getFieldType(associateTable, associateIdField));
            }

            if (ownerIdField && associateFieldType) {

                const column: SqlColumn = {
                    name: String(associateColumnName),
                    fieldType: associateFieldType
                };

                if (associateIdField) {
                    column.foreignKey = {
                        table: String(associateTable),
                        column: String(associateIdField)
                    };
                }

                definitions.associations[propertyNameStr] = {
                    name: String(fieldAssociation.name),
                    ownerIdColumn: {
                        name: fieldAssociation.ownerColumn.name,
                        fieldType: String(dtoModel.getFieldType(fieldAssociation.ownerColumn.foreignTable, ownerIdField)),
                        foreignKey: { table: String(fieldAssociation.ownerColumn.foreignTable), column: String(ownerIdField) }
                    },
                    columns: [column]
                };
            }
        }

        if (propertyNameStr.startsWith("__")) {
            // Skip properties starting with a double underscore
            continue;
        }

        const fieldType = dtoModel.getFieldType(tableName, propertyName);
        if (fieldType == null) {
            // If you get this error, look at the properties typing, it was not read by typeInfo<DTO>()
            throw `Can't get the type for property ${tableName as string}.${propertyName as string}`;
        }

        const column: SqlColumn<TableName, FieldTypeName> = {
            name: propertyNameStr,
            fieldType,
            isAssociativeColumn: Boolean(fieldAssociation)
        };

        const foreignKeyTable = dtoModel.getFieldForeignKey(<TableName>tableName, propertyName);
        if (dtoModel.isTableIdField(tableName, propertyName)) {
            // This is the id column
            column.isAutoGeneratedId = true;
        } else if (foreignKeyTable) {
            // We need to have a foreign key
            const foreignKey: SqlForeignKey<TableName> = {
                table: foreignKeyTable,
                column: typeof foreignKeyTable === "function" ? undefined : dtoModel.getTableIdField(foreignKeyTable) as string,
                weak: dtoModel.isFieldWeakForeignKey(<TableName>tableName, propertyName)
            };
            column.foreignKey = foreignKey;
        }

        column.isDeletionMarker = dtoModel.isTableArchivedField(tableName, propertyName) ? true : undefined;
        definitions.columns.push(column);
    }

    return definitions;
}

/** 
 * Build table definition map from the DTOModelService and marshallers 
 */
export function buildSQLTableDefinitionMapFromDTOModel<ET, FT>(marshallers: Map<keyof FT, string | FieldMarshaller>, dtoModel: DTOModelService<ET, FT>): SqlDbDefinitionMap<keyof ET, keyof FT> {
    const dbDefinitionMap: SqlDbDefinitionMap<keyof ET, keyof FT> = new Map();

    const dbDefinition = buildSQLDBDefinitionFromDTOModel(marshallers, dtoModel);
    for (const tableDefinition of dbDefinition.tables) {
        dbDefinitionMap.set(tableDefinition.name, tableDefinition);
    }
    return dbDefinitionMap;
}

//#endregion

//#region Get column information from table definition

/**
 * Get the foreign table to read upToDateId from (or null if not applicable).
 * Works on both standard properties and associative properties.
 * On standard properties, it will return the tablename designated by the foreign key wether it is weak or not as far as the target column is an autogenerated column.
 * On associative properties, it will return the first non-weak table designated as a foreign key (column is not tested).
 * 
 * @returns The foreign table name or null if elementTypeName.fieldName is not a foreign key.
 * @throws If column is not found or definition is invalid.
 * 
 * Tested in @cervval.js/common-action/src/services/cache.tools.spec.ts
 * Note : Here we cannot have cache because table may be dynamic.
 */
export function getForeignKeyTableName<ElementTypes, ET extends keyof ElementTypes>(tableDefinitionMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>>, elementTypeName: ET, fieldName: keyof ElementTypes[ET]): (keyof ElementTypes) | null {
    const column = getColumnDefinition(tableDefinitionMap, elementTypeName, fieldName);
    if (!column) {
        // Sometime we may pass fieldName that are not stored in DB.
        // For example in opCreate, if there is some cache fields in the cloned DTO
        return null;
    } else if (column.foreignKey == null) {
        if (!column.isAssociativeColumn) {
            // Not a foreign key and not an associative column
            return null;
        } else {
            // Check for associative column
            const associativeColumns = tableDefinitionMap.get(elementTypeName)?.associations?.[String(fieldName)].columns;
            if (associativeColumns == null) {
                // We require columns to get to the foreign key
                throw `Invalid definition for associative column ${elementTypeName as string}.${fieldName as string}`;
            }

            const typedAssociativeColumns = associativeColumns as SqlColumn<keyof ElementTypes, string>[]; // This variable is casted, since an association name must be typed as string (as an association might not be registered as an ElementType).

            // We will now look for the first foreign key column
            for (const associativeColumn of typedAssociativeColumns) {
                if (associativeColumn.foreignKey == null || associativeColumn.foreignKey.weak) {
                    // This is not a foreign key, skip this column
                    // If this is a weak foreign key, skip it also, it may reference another table, but is not the main target
                    continue;
                }

                const foreignTable = ObjectTools.getItemVOFValue<null, keyof ElementTypes>(null, associativeColumn.foreignKey?.table, { continueWithNullItem: true });
                if (foreignTable === undefined) {
                    throw new Error(`Failed to get the foreign table name for ${elementTypeName as string}.${fieldName as string}`);
                }

                return foreignTable;
            }

            // We do not need to throw an error if we don't find at least one foreign key constraint in typedAssociativeColumns. We could have
            // an associative table with ownerColumnId which is a foreignKey but other column is not a foreignKey 
            // (example : CDS/ZurvanTransport table userRoles, we have an associativeTable which references permissions by roles, and permissions in an enumeration)  
            return null;
        }
    } else {
        const foreignTable = ObjectTools.getItemVOFValue<null, keyof ElementTypes>(null, column.foreignKey?.table, { continueWithNullItem: true });
        if (foreignTable === undefined) {
            throw new Error(`Failed to get the foreign table name for ${elementTypeName as string}.${fieldName as string} through association`);
        }
        if (column.foreignKey.column) {
            // Check that foreign key targets an autogenerated id
            const foreignColumn = getColumnDefinition(tableDefinitionMap, foreignTable, column.foreignKey.column as keyof ElementTypes[typeof foreignTable]);
            if (foreignColumn == null) {
                throw new Error(`Failed to get the foreign column definition for ${foreignTable as string}.${column.foreignKey.column as string}`);
            } else if (!foreignColumn.isAutoGeneratedId) {
                // No need to update
                return null;
            }
        }

        return foreignTable;
    }
}

/** 
 * Get column definition from tableDefinition using a cache to speed up the process. 
 * @returns undefined if not found, SqlColumn else
 * 
 * Tested in @cervval.js/common-action/src/services/cache.tools.spec.ts
 */
export function getColumnDefinition<ElementTypes, ET extends keyof ElementTypes>(tableDefinitionMap: Map<keyof ElementTypes, SqlTableDefinition<keyof ElementTypes, string>>, elementTypeName: ET, fieldName: keyof ElementTypes[ET]): SqlColumn<ET, string> | undefined {
    const tableDefinition = tableDefinitionMap.get(elementTypeName);
    if (!tableDefinition) {
        getLogConsole().error(`Invalid table ${elementTypeName as string}`);
        return undefined;
    }
    for (const column of tableDefinition.columns) {
        if (column.name === fieldName) {
            return column as SqlColumn<ET, string>;
        }
    }
    getLogConsole().warn(`Column ${elementTypeName as string}.${fieldName as string} was not found`);
    return undefined;
}

//#endregion